import { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Chip,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { AudienceSegment, Catalogue, MeasurementEngine, FunnelMix } from '@traaker/shared';
import { AUDIENCE_LIMITS, DEFAULT_FUNNEL_MIX, SEGMENT_AVATARS, DEFAULT_SEGMENT_AVATAR } from '@traaker/shared';
import { DistributionEditor } from '../../components/brand/DistributionEditor';
import { EngineLogo } from '../../components/brand/EngineLogo';
import { useBrand } from '../../contexts/BrandContext';
import { Flag, ZoneFlag } from '../dashboard/Flag';
import { languageFlag, resolveZone } from '../dashboard/geoFlags';

const IMPORT_CSV_MAX_BYTES = 256 * 1024;

const FUNNEL_SEGMENTS = [
  { key: 'tofu' as const, label: 'TOFU · découverte', description: 'Exploration, problème conscient — options larges', color: '#8B9DC3' },
  { key: 'mofu' as const, label: 'MOFU · comparaison', description: 'Critères de choix, comparaison de produits', color: '#7C3AED' },
  { key: 'bofu' as const, label: 'BOFU · achat', description: 'Intention forte, prêt à acheter, où commander', color: '#00A88A' },
];

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];
const ENGINES: { id: MeasurementEngine; label: string }[] = [
  { id: 'ai_overview', label: 'AI Overview' },
  { id: 'openai', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'claude', label: 'Claude' },
  { id: 'mistral', label: 'Mistral' },
];

const EMPTY: AudienceSegment = {
  segmentId: '',
  name: '',
  avatar: DEFAULT_SEGMENT_AVATAR,
  description: '',
  languages: ['fr'],
  engines: ['openai', 'gemini'],
  zones: [],
  catalogueId: '',
  promptCount: 10,
  funnelMix: { ...DEFAULT_FUNNEL_MIX },
};

/** Configure one customer segment (semantic description + engines/langs/zones/#prompts/catalogue). */
export function SegmentEditorDialog({
  open,
  segment,
  catalogues,
  onClose,
  onSave,
}: {
  open: boolean;
  segment: AudienceSegment | null; // null = new
  catalogues: Catalogue[];
  onClose: () => void;
  /** `importCsv` is set only when the user picked "Importer (CSV)" mode and
   *  chose a file — the caller saves the segment first, then imports. */
  onSave: (s: AudienceSegment, importCsv?: string) => void;
}) {
  const [s, setS] = useState<AudienceSegment>(
    segment || {
      ...EMPTY,
      catalogueId: (catalogues.find((c) => c.lastOptimizedAt) ?? catalogues[0])?.catalogueId || '',
    },
  );
  const [mode, setMode] = useState<'generate' | 'import'>('generate');
  const [importCsv, setImportCsv] = useState<string | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [newZone, setNewZone] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { activeBrand } = useBrand();
  const set = (patch: Partial<AudienceSegment>) => setS((prev) => ({ ...prev, ...patch }));
  const toggle = <K extends 'languages' | 'engines' | 'zones'>(key: K, value: string) => {
    const arr = new Set((s[key] as string[]) || []);
    arr.has(value) ? arr.delete(value) : arr.add(value);
    set({ [key]: [...arr] } as any);
  };

  const selectedCat = catalogues.find((c) => c.catalogueId === s.catalogueId);
  // Union: catalogue's own geoZone ∪ the brand's Brand-Knowledge location
  // zones ∪ any zone already on the segment (free-text adds, item h).
  const zoneOptions = [
    ...new Set([
      ...(selectedCat?.geoZone ? [selectedCat.geoZone] : []),
      ...(activeBrand?.locations?.map((l) => l.zone).filter(Boolean) ?? []),
      ...(s.zones ?? []),
    ]),
  ];
  const addZone = () => {
    const z = newZone.trim().slice(0, 60);
    if (!z || (s.zones?.length ?? 0) >= 10 || s.zones?.includes(z)) return;
    set({ zones: [...(s.zones ?? []), z] });
    setNewZone('');
  };
  const valid =
    s.name.trim() &&
    s.description.trim() &&
    s.catalogueId &&
    !!selectedCat?.lastOptimizedAt &&
    s.engines.length &&
    s.languages.length &&
    (mode === 'generate' || !!importCsv);

  const downloadPromptTemplate = () => {
    const csv = 'prompt;language;zone\n' +
      'Quel est le meilleur produit pour ce besoin ?;fr;\n' +
      'Ou acheter ce type de produit pas cher ?;fr;\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele-prompts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPickImportFile = (file?: File) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) return;
    if (file.size > IMPORT_CSV_MAX_BYTES) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImportCsv(String(reader.result || ''));
      setImportFileName(file.name);
    };
    reader.readAsText(file);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{segment ? 'Modifier le segment' : 'Nouveau segment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
                flexShrink: 0,
              }}
            >
              {s.avatar || DEFAULT_SEGMENT_AVATAR}
            </Box>
            <TextField
              label="Nom du segment"
              value={s.name}
              onChange={(e) => set({ name: e.target.value })}
              size="small"
              fullWidth
              placeholder="Parents pressés"
            />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Icône
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxHeight: 116,
                overflowY: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.75,
              }}
            >
              {SEGMENT_AVATARS.map((a) => (
                <Box
                  key={a}
                  onClick={() => set({ avatar: a })}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: s.avatar === a ? 'primary.main' : 'transparent',
                    bgcolor: s.avatar === a ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {a}
                </Box>
              ))}
            </Box>
          </Box>
          <TextField
            label="Description du client (qui est-il, ce qu'il cherche)"
            value={s.description}
            onChange={(e) => set({ description: e.target.value })}
            multiline
            minRows={3}
            fullWidth
            size="small"
            placeholder="Jeune parent, achats rapides, cherche des basiques fiables livrables le jour même, sensible au prix."
            helperText="Description sémantique — elle guide la génération des prompts."
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Catalogue</InputLabel>
            <Select
              label="Catalogue"
              value={s.catalogueId}
              onChange={(e) => set({ catalogueId: e.target.value, zones: [] })}
            >
              {catalogues.map((c) => {
                const notOptimized = !c.lastOptimizedAt;
                return (
                  <MenuItem key={c.catalogueId} value={c.catalogueId} disabled={notOptimized}>
                    {c.name} ({c.totalProducts} produits)
                    {notOptimized && ' — non optimisé'}
                  </MenuItem>
                );
              })}
            </Select>
            {catalogues.some((c) => !c.lastOptimizedAt) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Un catalogue "non optimisé" doit d'abord être analysé (onglet Catalogue) — sans cela,
                les noms/SKU produits ne sont pas correctement extraits et la mesure sera peu fiable.
              </Typography>
            )}
          </FormControl>

          <Box>
            <Typography variant="caption" color="text.secondary">Langues</Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              {LANGUAGES.map((l) => (
                <Chip
                  key={l.code}
                  icon={<Flag countryCode={languageFlag(l.code)} size={16} />}
                  label={l.label}
                  size="small"
                  color={s.languages.includes(l.code) ? 'primary' : 'default'}
                  variant={s.languages.includes(l.code) ? 'filled' : 'outlined'}
                  onClick={() => toggle('languages', l.code)}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">Moteurs IA</Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              {ENGINES.map((e) => {
                const selected = s.engines.includes(e.id);
                return (
                  <Box
                    key={e.id}
                    role="button"
                    title={e.label}
                    aria-label={e.label}
                    aria-pressed={selected}
                    onClick={() => toggle('engines', e.id)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: '1.5px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      // White inner tile keeps brand-colored logos legible on the
                      // filled (selected) primary background.
                      bgcolor: selected ? 'primary.main' : '#fff',
                      transition: 'all .12s',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        bgcolor: selected ? '#fff' : 'transparent',
                        p: selected ? '2px' : 0,
                      }}
                    >
                      <EngineLogo engine={e.id} size={16} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Prompts
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={mode}
              onChange={(_, v) => v && setMode(v)}
              sx={{ mt: 0.5 }}
            >
              <ToggleButton value="generate">Générer</ToggleButton>
              <ToggleButton value="import">Importer (CSV)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {mode === 'generate' ? (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Nombre de prompts : {s.promptCount}
                </Typography>
                <Slider
                  value={s.promptCount}
                  min={1}
                  max={AUDIENCE_LIMITS.promptsPerSegment}
                  onChange={(_, v) => set({ promptCount: v as number })}
                  valueLabelDisplay="auto"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  ≈ {s.promptCount * s.engines.length} requêtes IA (prompts × moteurs) par audit.
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Zones (optionnel)</Typography>
                {zoneOptions.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {zoneOptions.map((z) => (
                      <Chip
                        key={z}
                        icon={<ZoneFlag zone={resolveZone(z)} size={16} />}
                        label={z}
                        size="small"
                        color={s.zones?.includes(z) ? 'primary' : 'default'}
                        variant={s.zones?.includes(z) ? 'filled' : 'outlined'}
                        onClick={() => toggle('zones', z)}
                      />
                    ))}
                  </Stack>
                )}
                {(s.zones?.length ?? 0) < 10 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                    <TextField
                      size="small"
                      placeholder="Ajouter une zone"
                      value={newZone}
                      onChange={(e) => setNewZone(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addZone();
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button size="small" onClick={addZone} disabled={!newZone.trim()}>
                      Ajouter
                    </Button>
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Étape du parcours d'achat (influence les prompts générés)
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <DistributionEditor
                    segments={FUNNEL_SEGMENTS}
                    value={(s.funnelMix || DEFAULT_FUNNEL_MIX) as unknown as Record<'tofu' | 'mofu' | 'bofu', number>}
                    onChange={(next) => set({ funnelMix: next as unknown as FunnelMix })}
                    compact
                  />
                </Box>
              </Box>
            </>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 1 }}>
                L'import remplace les prompts existants de ce segment.
              </Alert>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choisir un fichier .csv
                </Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={downloadPromptTemplate}>
                  Modèle
                </Button>
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => onPickImportFile(e.target.files?.[0])}
              />
              {importFileName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {importFileName}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Colonnes : prompt (obligatoire) · language · zone. Max 100 lignes.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onSave(s, mode === 'import' ? importCsv || undefined : undefined)}
        >
          {mode === 'import' ? 'Enregistrer et importer' : 'Enregistrer le segment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
